// src/context/AuthContext.jsx - FINAL VERSION (Using Firestore isAdmin)

import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase';  
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";  

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null); // Add userData state for real-time sync
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false); // Stores Admin status from Firestore

  // Google Login
  const loginWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider); 
  };

  // Logout function
  const logout = () => {
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setIsAdmin(false); // Reset admin status initially
      
      if (!user) {
        setUserData(null); // Clear userData when logged out
      }

      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
              const data = userDoc.data();
              // ✅ Read the isAdmin boolean field from Firestore
              setIsAdmin(data.isAdmin === true); 
          } else {
              // Create new user profile if it doesn't exist
            const newUserData = {
              uid: user.uid,
              username: user.displayName || "",
              email: user.email || "",
              mobile: user.phoneNumber || "",
              phoneNumber: user.phoneNumber || "",
              class: "",
              preparingFor: "",
              isAdmin: false, // Default to false
              photoURL: user.photoURL || "",
              authMethod: user.providerData[0]?.providerId || "unknown",
              createdAt: new Date().toISOString(),
              purchasedCourses: [], // ✅ Initialize empty array for purchases
              testsTaken: [], // ✅ Initialize empty array for test history
            };
            await setDoc(userRef, newUserData);
            setIsAdmin(false);
          }
        } catch (error) {
          console.error("Error ensuring user profile:", error);
        }
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Add real-time listener for userData (like PrepXPathshala)
  useEffect(() => {
    let unsubscribeFirestore;
    
    if (currentUser) {
      const userRef = doc(db, "users", currentUser.uid);
      
      // Real-time listener for user data changes
      unsubscribeFirestore = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(data);
          // Also update isAdmin from real-time data
          setIsAdmin(data.isAdmin === true);
        } else {
          setUserData(null);
        }
      }, (error) => {
        console.error("Error listening to user data:", error);
        setUserData(null);
      });
    } else {
      setUserData(null);
    }

    return () => {
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
      }
    };
  }, [currentUser]);

  const value = {
    currentUser,
    userData, // ✅ Expose userData for profile completion tracking
    loading,
    isAdmin, // EXPOSE isAdmin STATUS
    loginWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
